import { useEffect, useState } from 'react';
import api from '../api/client.js';
import { useToast } from '../context/ToastContext.jsx';
import { crc } from '../utils/format.js';
import { PageHeader, Modal, Loading, EmptyState } from '../components/UI.jsx';
import { Icon } from '../components/Icons.jsx';
import CabysPicker from '../components/CabysPicker.jsx';

const IVA_OPCIONES = [13, 4, 2, 1, 0];

const cabysOk = (c) => /^\d{13}$/.test(String(c || '')) && c !== '0000000000000';

export default function Inventario() {
  const toast = useToast();
  const [productos, setProductos] = useState(null);
  const [categorias, setCategorias] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [filtroCat, setFiltroCat] = useState('');
  const [soloStockBajo, setSoloStockBajo] = useState(false);
  const [soloSinCabys, setSoloSinCabys] = useState(false);
  // Fecha de alta del producto. Vacio por defecto: el catalogo se ve entero.
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [resumenCabys, setResumenCabys] = useState(null);
  // El CAByS solo sirve para los comprobantes electronicos. Con la factura
  // electronica apagada no se pide, no se muestra y no estorba.
  const [feActiva, setFeActiva] = useState(false);

  const [modalProd, setModalProd] = useState(false);
  const [modalCat, setModalCat] = useState(false);
  const [modalStock, setModalStock] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({});
  const [catForm, setCatForm] = useState({ nombre: '', color: '#5b21b6' });
  const [stockForm, setStockForm] = useState({ cantidad: '', tipo: 'ajuste', motivo: '' });
  // Alta de categoria sin salir del formulario de producto
  const [catRapida, setCatRapida] = useState('');
  const [creandoCat, setCreandoCat] = useState(false);

  const cargar = () => {
    const params = {};
    if (busqueda) params.q = busqueda;
    if (filtroCat) params.categoria = filtroCat;
    if (soloStockBajo) params.stockBajo = 'true';
    if (soloSinCabys) params.sinCabys = 'true';
    if (desde) params.desde = desde;
    if (hasta) params.hasta = hasta;
    api.get('/productos', { params }).then((r) => setProductos(r.data.data));
    api.get('/categorias').then((r) => setCategorias(r.data.data));
    api.get('/productos-cabys/resumen').then((r) => setResumenCabys(r.data.data)).catch(() => {});
    api.get('/empresa').then((r) => setFeActiva(!!r.data.data?.fe_activa)).catch(() => {});
  };
  useEffect(cargar, [busqueda, filtroCat, soloStockBajo, soloSinCabys, desde, hasta]);

  // Si se apaga la FE con el filtro "sin CAByS" puesto, se suelta el filtro.
  useEffect(() => {
    if (!feActiva && soloSinCabys) setSoloSinCabys(false);
  }, [feActiva, soloSinCabys]);

  const abrirNuevo = () => {
    setEditando(null);
    setCatRapida('');
    setForm({
      nombre: '', sku: '', codigo_cabys: '', categoria_id: '',
      precio_costo: '', precio_venta: '', tarifa_iva: 13, stock: 0, stock_minimo: 5,
    });
    setModalProd(true);
  };
  const abrirEditar = (p) => {
    setEditando(p.id);
    setCatRapida('');
    setForm({ ...p, categoria_id: p.categoria_id || '' });
    setModalProd(true);
  };

  // Crea la categoria y la deja seleccionada, sin cerrar el producto.
  const crearCatRapida = async () => {
    const nombre = catRapida.trim();
    if (!nombre) return;
    setCreandoCat(true);
    try {
      const { data } = await api.post('/categorias', { nombre });
      const nueva = data.data;
      setCategorias((prev) => [...prev, nueva].sort((a, b) => String(a.nombre).localeCompare(String(b.nombre), 'es')));
      setForm((f) => ({ ...f, categoria_id: String(nueva.id) }));
      setCatRapida('');
      toast.success(`Categoria "${nueva.nombre}" creada`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'No se pudo crear la categoria');
    } finally {
      setCreandoCat(false);
    }
  };

  const guardarProd = async () => {
    if (!form.nombre) { toast.error('El nombre es requerido'); return; }
    const cabys = String(form.codigo_cabys || '').replace(/\D/g, '');
    if (cabys && !cabysOk(cabys)) {
      toast.error('El CAByS debe tener exactamente 13 digitos numericos');
      return;
    }
    try {
      const payload = {
        ...form,
        categoria_id: form.categoria_id || null,
        codigo_cabys: cabys,
      };
      if (editando) await api.put(`/productos/${editando}`, payload);
      else await api.post('/productos', payload);
      toast.success(editando ? 'Producto actualizado' : 'Producto creado');
      setModalProd(false);
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error al guardar');
    }
  };

  const eliminarProd = async (p) => {
    if (!confirm(`Eliminar ${p.nombre}?`)) return;
    await api.delete(`/productos/${p.id}`);
    toast.success('Producto eliminado');
    cargar();
  };

  const guardarCat = async () => {
    if (!catForm.nombre) { toast.error('Nombre requerido'); return; }
    try {
      await api.post('/categorias', catForm);
      toast.success('Categoria creada');
      setCatForm({ nombre: '', color: '#5b21b6' });
      api.get('/categorias').then((r) => setCategorias(r.data.data));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error');
    }
  };

  const eliminarCat = async (id) => {
    if (!confirm('Eliminar esta categoria?')) return;
    await api.delete(`/categorias/${id}`);
    api.get('/categorias').then((r) => setCategorias(r.data.data));
  };

  const abrirStock = (p) => {
    setEditando(p);
    setStockForm({ cantidad: '', tipo: 'ajuste', motivo: '' });
    setModalStock(true);
  };
  const guardarStock = async () => {
    const cant = Number(stockForm.cantidad);
    if (!cant) { toast.error('Cantidad invalida'); return; }
    try {
      await api.post(`/productos/${editando.id}/stock`, stockForm);
      toast.success('Stock ajustado');
      setModalStock(false);
      cargar();
    } catch {
      toast.error('Error al ajustar');
    }
  };

  return (
    <div>
      <PageHeader title="Inventario" subtitle="Productos, existencias, CAByS y categorias">
        <button className="btn btn-outline" onClick={() => setModalCat(true)}>
          <Icon.inventario /> Categorias
        </button>
        <button className="btn btn-primary" onClick={abrirNuevo}>
          <Icon.plus /> Nuevo producto
        </button>
      </PageHeader>

      {feActiva && resumenCabys && Number(resumenCabys.sin_cabys) > 0 && (
        <div
          className="card"
          style={{
            padding: 12,
            marginBottom: 12,
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            flexWrap: 'wrap',
            borderColor: 'var(--warning)',
            background: 'var(--warning-bg)',
          }}
        >
          <Icon.alert width={18} height={18} />
          <span style={{ flex: 1, fontSize: 14 }}>
            <strong>{resumenCabys.sin_cabys}</strong> de {resumenCabys.activos} producto(s) sin CAByS valido.
            Hacienda lo exige en factura/tiquete electronico v4.4.
          </span>
          <button
            className="btn btn-sm btn-outline"
            onClick={() => { setSoloSinCabys(true); setSoloStockBajo(false); }}
          >
            Ver sin CAByS
          </button>
        </div>
      )}

      <div className="card" style={{ padding: 14, marginBottom: 16, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }}>
            <Icon.search width={18} height={18} />
          </span>
          <input
            className="input"
            style={{ paddingLeft: 38 }}
            placeholder={feActiva ? 'Buscar nombre, SKU o CAByS…' : 'Buscar nombre o SKU…'}
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
        </div>
        <select className="select" style={{ width: 'auto' }} value={filtroCat} onChange={(e) => setFiltroCat(e.target.value)}>
          <option value="">Todas las categorias</option>
          {categorias.map((c) => (
            <option key={c.id} value={c.id}>{c.nombre}</option>
          ))}
        </select>
        {/* Con texto visible: dos fechas sueltas no dicen que filtran. */}
        <span className="flex items-center gap-sm" style={{ flexWrap: 'wrap' }}>
          <span className="muted" style={{ fontSize: 13 }}>Dado de alta</span>
          <input className="input" type="date" value={desde} title="Dado de alta desde"
            aria-label="Dado de alta desde" style={{ width: 'auto' }}
            onChange={(e) => setDesde(e.target.value)} />
          <span className="muted" style={{ fontSize: 13 }}>a</span>
          <input className="input" type="date" value={hasta} title="Dado de alta hasta"
            aria-label="Dado de alta hasta" style={{ width: 'auto' }}
            onChange={(e) => setHasta(e.target.value)} />
        </span>
        {(desde || hasta) && (
          <button className="btn btn-ghost btn-sm" onClick={() => { setDesde(''); setHasta(''); }}>
            Quitar fechas
          </button>
        )}
        <button
          className={`btn ${soloStockBajo ? 'btn-primary' : 'btn-outline'}`}
          onClick={() => { setSoloStockBajo((s) => !s); setSoloSinCabys(false); }}
        >
          <Icon.alert width={16} height={16} /> Stock bajo
        </button>
        {feActiva && (
          <button
            className={`btn ${soloSinCabys ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => { setSoloSinCabys((s) => !s); setSoloStockBajo(false); }}
          >
            Sin CAByS
            {resumenCabys?.sin_cabys > 0 && (
              <span className="badge" style={{ marginLeft: 6, background: 'var(--warning-bg)', color: 'var(--warning)' }}>
                {resumenCabys.sin_cabys}
              </span>
            )}
          </button>
        )}
      </div>

      {!productos ? (
        <Loading />
      ) : productos.length === 0 ? (
        <EmptyState icon="inventario" titulo="Sin productos" texto="Crea tu primer producto para empezar." />
      ) : (
        <div className="card table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Producto</th>
                {feActiva && <th>CAByS</th>}
                <th>Categoria</th>
                <th className="text-right">Costo</th>
                <th className="text-right">Precio</th>
                <th className="text-center">IVA</th>
                <th className="text-center">Stock</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {productos.map((p) => {
                const ok = cabysOk(p.codigo_cabys);
                return (
                  <tr key={p.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{p.nombre}</div>
                      {p.sku && <div className="muted" style={{ fontSize: 12 }}>{p.sku}</div>}
                    </td>
                    {feActiva && (
                      <td>
                        {ok ? (
                          <span className="mono" style={{ fontSize: 12 }}>{p.codigo_cabys}</span>
                        ) : (
                          <button
                            type="button"
                            className="badge"
                            style={{ cursor: 'pointer', background: 'var(--warning-bg)', color: 'var(--warning)', border: 0 }}
                            onClick={() => abrirEditar(p)}
                            title="Asignar CAByS"
                          >
                            Falta CAByS
                          </button>
                        )}
                      </td>
                    )}
                    <td>
                      {p.categoria_nombre ? (
                        <span className="badge" style={{ background: 'var(--accent-soft)', color: 'var(--accent-texto)' }}>
                          {p.categoria_nombre}
                        </span>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td className="text-right mono">{crc(p.precio_costo)}</td>
                    <td className="text-right mono" style={{ fontWeight: 600 }}>{crc(p.precio_venta)}</td>
                    <td className="text-center">{Number(p.tarifa_iva)}%</td>
                    <td className="text-center">
                      <button
                        onClick={() => abrirStock(p)}
                        className="badge"
                        style={{
                          cursor: 'pointer',
                          background: Number(p.stock) <= Number(p.stock_minimo) ? 'var(--warning-bg)' : 'var(--success-bg)',
                          color: Number(p.stock) <= Number(p.stock_minimo) ? 'var(--warning)' : 'var(--success)',
                        }}
                      >
                        {Number(p.stock)} {p.unidad_medida}
                      </button>
                    </td>
                    <td>
                      <div className="flex gap-sm" style={{ justifyContent: 'flex-end' }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => abrirEditar(p)} title="Editar">
                          <Icon.edit width={15} height={15} />
                        </button>
                        <button className="btn btn-sm" style={{ color: 'var(--danger)' }} onClick={() => eliminarProd(p)} title="Eliminar">
                          <Icon.trash width={15} height={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={modalProd} onClose={() => setModalProd(false)} title={editando ? 'Editar producto' : 'Nuevo producto'} width={640}>
        <div className="grid-2" style={{ gap: 12 }}>
          <div className="field" style={{ gridColumn: '1/-1' }}>
            <label>Nombre *</label>
            <input className="input" value={form.nombre || ''} onChange={(e) => setForm({ ...form, nombre: e.target.value })} />
          </div>
          <div className="field">
            <label>SKU</label>
            <input className="input" value={form.sku || ''} onChange={(e) => setForm({ ...form, sku: e.target.value })} />
          </div>
          <div className="field">
            <label>Categoria</label>
            <select className="select" value={form.categoria_id || ''} onChange={(e) => setForm({ ...form, categoria_id: e.target.value })}>
              <option value="">Sin categoria</option>
              {categorias.map((c) => (
                <option key={c.id} value={c.id}>{c.nombre}</option>
              ))}
            </select>
            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
              <input
                className="input"
                placeholder="Crear categoria nueva"
                value={catRapida}
                onChange={(e) => setCatRapida(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); crearCatRapida(); }
                }}
              />
              <button
                type="button"
                className="btn btn-outline"
                style={{ flexShrink: 0, padding: '0 12px' }}
                onClick={crearCatRapida}
                disabled={!catRapida.trim() || creandoCat}
                title="Crear y usar esta categoria"
              >
                <Icon.plus width={16} height={16} />
              </button>
            </div>
          </div>

          {/* El CAByS solo lo exige Hacienda en los comprobantes electronicos. */}
          {feActiva && (
            <CabysPicker
              value={form.codigo_cabys || ''}
              onChange={(codigo) => setForm((f) => ({ ...f, codigo_cabys: codigo }))}
              onSelectMeta={(m) => {
                if (m?.tarifa_iva != null && m.tarifa_iva !== '') {
                  setForm((f) => ({ ...f, tarifa_iva: Number(m.tarifa_iva) }));
                }
              }}
              label="Codigo CAByS (factura electronica)"
            />
          )}

          <div className="field">
            <label>Tarifa de IVA</label>
            <select
              className="select"
              value={form.tarifa_iva ?? 13}
              onChange={(e) => setForm({ ...form, tarifa_iva: Number(e.target.value) })}
            >
              {IVA_OPCIONES.map((t) => (
                <option key={t} value={t}>{t}%</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Precio de costo (₡)</label>
            <input className="input" type="number" value={form.precio_costo ?? ''} onChange={(e) => setForm({ ...form, precio_costo: e.target.value })} />
          </div>
          <div className="field">
            <label>Precio de venta (₡)</label>
            <input className="input" type="number" value={form.precio_venta ?? ''} onChange={(e) => setForm({ ...form, precio_venta: e.target.value })} />
          </div>
          <div className="field">
            <label>Stock minimo</label>
            <input className="input" type="number" value={form.stock_minimo ?? 5} onChange={(e) => setForm({ ...form, stock_minimo: e.target.value })} />
          </div>
          {!editando && (
            <div className="field">
              <label>Stock inicial</label>
              <input className="input" type="number" value={form.stock ?? 0} onChange={(e) => setForm({ ...form, stock: e.target.value })} />
            </div>
          )}
        </div>
        {feActiva && (
          <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
            El CAByS de 13 digitos es obligatorio para factura y tiquete electronico. Catalogo completo en el BCCR.
          </p>
        )}
        <div className="flex gap-sm" style={{ justifyContent: 'flex-end', marginTop: 10 }}>
          <button className="btn btn-outline" onClick={() => setModalProd(false)}>Cancelar</button>
          <button className="btn btn-primary" onClick={guardarProd}>Guardar</button>
        </div>
      </Modal>

      <Modal open={modalCat} onClose={() => setModalCat(false)} title="Categorias">
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <input className="input" placeholder="Nueva categoria" value={catForm.nombre} onChange={(e) => setCatForm({ ...catForm, nombre: e.target.value })} />
          <input type="color" value={catForm.color} onChange={(e) => setCatForm({ ...catForm, color: e.target.value })} style={{ width: 46, borderRadius: 8, border: '1px solid var(--border)', cursor: 'pointer' }} />
          <button className="btn btn-primary" onClick={guardarCat} style={{ flexShrink: 0 }}><Icon.plus /></button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {categorias.map((c) => (
            <div key={c.id} className="flex items-center justify-between" style={{ padding: '8px 12px', borderRadius: 8, background: 'var(--surface-2)' }}>
              <span className="flex items-center gap-sm">
                <span style={{ width: 12, height: 12, borderRadius: '50%', background: c.color }} />
                {c.nombre}
              </span>
              <span className="flex items-center gap-sm">
                <span className="muted" style={{ fontSize: 12 }}>{c.total_productos} prod.</span>
                <button className="btn btn-sm" style={{ color: 'var(--danger)' }} onClick={() => eliminarCat(c.id)}>
                  <Icon.trash width={14} height={14} />
                </button>
              </span>
            </div>
          ))}
        </div>
      </Modal>

      <Modal open={modalStock} onClose={() => setModalStock(false)} title={`Ajustar stock: ${editando?.nombre || ''}`} width={420}>
        <p className="muted" style={{ marginBottom: 14 }}>
          Stock actual: <strong>{Number(editando?.stock || 0)}</strong>. Usa positivo para sumar, negativo para restar.
        </p>
        <div className="field">
          <label>Cantidad (+/−)</label>
          <input className="input" type="number" value={stockForm.cantidad} onChange={(e) => setStockForm({ ...stockForm, cantidad: e.target.value })} />
        </div>
        <div className="field">
          <label>Tipo</label>
          <select className="select" value={stockForm.tipo} onChange={(e) => setStockForm({ ...stockForm, tipo: e.target.value })}>
            <option value="ajuste">Ajuste</option>
            <option value="merma">Merma</option>
            <option value="compra">Entrada</option>
          </select>
        </div>
        <div className="field">
          <label>Motivo</label>
          <input className="input" value={stockForm.motivo} onChange={(e) => setStockForm({ ...stockForm, motivo: e.target.value })} />
        </div>
        <div className="flex gap-sm" style={{ justifyContent: 'flex-end' }}>
          <button className="btn btn-outline" onClick={() => setModalStock(false)}>Cancelar</button>
          <button className="btn btn-primary" onClick={guardarStock}>Aplicar</button>
        </div>
      </Modal>
    </div>
  );
}
